// ADMIN — the system's own record. The one page whose SUBJECT is Signet.
//
// What it replaced: two tabs. A settings form of six keys, five of which nothing read, whose
// one live key wrote ATTEST's accent ramp INLINE ON <html> from a page mount — so opening this
// page retinted the whole app for the session, away from a certified 7.58:1 and onto whatever
// hex was stored, with a six-digit regex as the only guard. And the audit kit's own viewer: a
// table of 1 630 rows filtered by a dropdown offering GET / POST / PUT / PATCH / DELETE, of
// which ZERO rows carry any; a details column that printed JSON; an IP column that is empty by
// construction because the manual logger takes no IP; and two free date inputs that could
// express a range with nothing in it, and usually did.
//
// Four zones, in this order:
//
//   POSTURE      Five claims about the door, each closed by the column that answers it, each
//                with the control that changes it when it is wrong. Registration mode, the
//                allowlist, the accounts that exist, the agent keys that can reach the tool
//                surface, and the client realm. This is not a directory to administer — it is a
//                standing statement, and a solo operator could not previously get ANY of it
//                without psql.
//
//   CONSEQUENCE  Of every entry, which ones were a DECISION? Three predicates over columns that
//                already exist: it carries a reason a human typed, it destroyed a row whose only
//                surviving copy is the entry itself, or it produced a hash a signature stands
//                on. Each card filters the trail to its own membership.
//
//   THE TRAIL    The real vocabulary — every verb that has occurred, grouped by the noun the
//                action string already carries, each with its count, so a filter that can
//                produce nothing says so BEFORE it is clicked. A band of real days with real
//                counts instead of two date inputs. One composed SENTENCE per row (from
//                src/server/utils/auditNarrate.ts, so the CSV export says the same thing), the
//                before/after diff reduced to the keys that actually moved, and the citation —
//                five columns and a copy button, which is the whole point of an audit log.
//
//   SWITCHES     Every setting with the list of code sites that READ it. A key nothing reads
//                says so, in the operator's face, instead of presenting itself as a working
//                switch. Save appears only when something is dirty: a control with nothing to
//                control is a display.
//
// The record's voice (ATTEST LAW 1) is decided by a COUNT: a note string that occurs exactly
// once in the whole table is a sentence the operator typed, and gets the serif and a source
// ref; a string on two or more rows is one of the constants the server writes, and gets the
// system's sans. Eleven authored, five templates, over 1 647 rows.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AA, ATTEST_DEFAULT_ACT, deriveAccent, isHex, nearestPassing } from '../../shared/accent.js';
import { useAuth } from 'auth-sso-kit/client/index.js';

import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll, POLL_MS } from '../hooks/usePoll.js';
import { useT, useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './AdminPage.de.js';
import './AdminPage.fr.js';
import {
  ROUTES, resolveRouteRef, scrollToAnchor, useDeepLink, useOpenRoute, useRouteParams,
} from '../routes.js';
import { degradedRail, type RailCell, type RailTone } from '../../shared/rail.js';
import {
  adminConsoleApi,
  type ConsequenceClass, type Piece, type Posture, type SettingRow,
  type TrailPage, type TrailRow, type Verb, type Vocabulary,
} from '../api/adminConsole.js';

// ── the rail: fixed set, fixed order, invariant geometry ────────────────────

type AdmRailKey = 'REALM' | 'ACCOUNTS' | 'AGENT KEYS' | 'SWITCHES' | 'TRAIL';
const RAIL_KEYS: readonly AdmRailKey[] = ['REALM', 'ACCOUNTS', 'AGENT KEYS', 'SWITCHES', 'TRAIL'];

interface AdmRailCell extends RailCell {
  key: AdmRailKey;
  /** The zone this cell is the headline of. Every cell on this page routes in-page. */
  anchor: string;
  anchorLabel: string;
  /** Mono, not tabular sans: ALLOWLIST is an identity, not a quantity (LAW 1). */
  token?: boolean;
}

const TONE_CLASS: Partial<Record<RailTone, string>> = {
  warn: ' fv--attend',
  attend: ' fv--attend',
  err: ' fv--broken',
  broken: ' fv--broken',
  unknown: ' adm-fv--unknown',
};

// ── small formatters ────────────────────────────────────────────────────────

/** 1647 → '1 647'. A thin space, because a comma is a decimal point somewhere. */
const num = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/** Postgres/ISO timestamp → 'DD.MM.YYYY'. Slice, never Date — a Date applies a timezone. */
const dmy = (ts: string): string => `${ts.slice(8, 10)}.${ts.slice(5, 7)}.${ts.slice(0, 4)}`;
const hm = (ts: string): string => ts.slice(11, 16);
const dm = (isoDay: string): string => `${isoDay.slice(8, 10)}.${isoDay.slice(5, 7)}.`;

const Fig = ({ children }: { children: React.ReactNode }) => <span className="voice-figure">{children}</span>;
const Ref = ({ children }: { children: React.ReactNode }) => <span className="src-ref">{children}</span>;

/**
 * One composed sentence, rendered in the voices ATTEST assigns.
 *
 * The server decided the WORDS (so the CSV export says the same thing); this decides only the
 * typeface, which is what "the page arranges, it does not derive" means here.
 */
function Line({ pieces }: { pieces: Piece[] }) {
  return (
    <>
      {pieces.map((p, i) => {
        if (p.k === 'none') return <span className="n-none" key={i}>—</span>;
        if (p.k === 'ref') return <Ref key={i}>{p.v}</Ref>;
        if (p.k === 'fig') return <Fig key={i}>{p.v}</Fig>;
        if (p.k === 'quote') return <span className="adm-q" key={i}>“{p.v}”</span>;
        if (p.k === 'em') {
          return (
            <b key={i} style={p.tone ? { color: `var(--${p.tone === 'broken' ? 'broken' : 'attend'})` } : undefined}>
              {p.v}
            </b>
          );
        }
        return <span key={i}>{p.v}</span>;
      })}
    </>
  );
}

// ── the diff (IMPLEMENTATION §3.5) ──────────────────────────────────────────

/**
 * A value, formatted by a four-branch dispatch, all deterministic.
 *
 * A hash is NEVER truncated: half a hash is worse than no hash, because it cannot be checked
 * and it looks as though it can.
 */
function DiffValue({ v }: { v: unknown }) {
  if (v === null || v === undefined) return <span className="n-none">—</span>;
  if (typeof v === 'object') return <Ref>{JSON.stringify(v).slice(0, 90)}</Ref>;
  if (typeof v === 'boolean') return <Ref>{String(v)}</Ref>;
  const s = String(v);
  if (/^[0-9a-f]{32,}$/.test(s)) return <Ref>{s}</Ref>;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return <Fig>{dmy(s)}</Fig>;
  if (typeof v === 'number') return <Fig>{s}</Fig>;
  return <>{s}</>;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Only the keys that actually MOVED. A diff that prints unchanged fields is a JSON dump with borders. */
function diffOf(details: Record<string, unknown> | null) {
  const d = details ?? {};
  const before = isObj(d.before) ? d.before : {};
  const after = isObj(d.after) ? d.after : {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((k) => k !== 'assertion_reason' && k !== 'note' && k !== 'reason');
  // Scalar siblings of before/after are the ENTRY's own facts, not a change — they render as a
  // meta run under the grid rather than pretending to be a field that moved.
  const scalars = Object.entries(d).filter(
    ([k, v]) => !['before', 'after', 'note', 'reason'].includes(k) && (v === null || typeof v !== 'object'),
  );
  return { keys, before, after, scalars };
}

// ── the verb catalogue ──────────────────────────────────────────────────────

interface Family { fam: string; n: number; verbs: Verb[]; last: string }

function familiesOf(verbs: Verb[]): Family[] {
  const m = new Map<string, Family>();
  for (const v of verbs) {
    const fam = v.action.split('.')[0];
    let f = m.get(fam);
    if (!f) { f = { fam, n: 0, verbs: [], last: '' }; m.set(fam, f); }
    f.n += v.n;
    f.verbs.push(v);
    if (v.last > f.last) f.last = v.last;
  }
  const out = [...m.values()];
  for (const f of out) f.verbs.sort((a, b) => b.n - a.n || a.action.localeCompare(b.action));
  out.sort((a, b) => b.n - a.n || a.fam.localeCompare(b.fam));
  return out;
}

// ── posture claims ──────────────────────────────────────────────────────────

type Lamp = 'ok' | 'attend' | 'off' | 'unknown';

interface Claim {
  id: string;
  lamp: Lamp;
  claim: React.ReactNode;
  ev: React.ReactNode[];
  act?: React.ReactNode;
}

// ── the removal note (SWITCHES) ─────────────────────────────────────────────
//
// ── the accent control: one exact colour, judged live by the same maths the server applies ──

/** After a save, the linked stylesheet is re-fetched — the tint changes at once, still with no inline style. */
function reloadThemeCss(): void {
  const link = document.querySelector<HTMLLinkElement>('link[href^="/theme.css"]');
  if (link) link.href = `/theme.css?t=${Date.now()}`;
}

function AccentInput({ value, disabled, onChange }: { value: string; disabled: boolean; onChange: (v: string) => void }) {
  const t = useT();
  const hex = isHex(value) ? value : ATTEST_DEFAULT_ACT;
  return (
    <div className="adm-accent-in">
      <input
        type="color"
        value={hex.toLowerCase()}
        disabled={disabled}
        aria-label={t('Pick the accent colour')}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
      />
      <input
        value={value}
        disabled={disabled}
        spellCheck={false}
        aria-label={t('Accent colour as hex')}
        placeholder={ATTEST_DEFAULT_ACT}
        onChange={(e) => onChange(e.target.value.trim())}
      />
      {value ? (
        <button type="button" className="lnkb" disabled={disabled} onClick={() => onChange('')} title={t("Back to ATTEST's own accent")}>{t("ATTEST's own")}</button>
      ) : null}
    </div>
  );
}

/** The six surfaces ATTEST measures — English keys, wrapped at the render site. */
const AA_SURFACES = [
  'day · a link on the field',
  'day · a link on the sheet',
  'day · text on a filled control',
  'night · a link on the field',
  'night · a link on the sheet',
  'night · text on a filled control',
] as const;

/** The ramp both themes will wear and the six ratios ATTEST measures — before you save. */
function AccentPreview({ value, onPick }: { value: string; onPick: (hex: string) => void }) {
  const t = useT();
  const hex = value.trim();
  if (!hex) {
    return (
      <div className="adm-accent">
        <span className="adm-accent-verdict pass">{t("ATTEST's own iron-gall blue")} <Ref>{ATTEST_DEFAULT_ACT}</Ref> {t('is in force — 6.78:1 on the field, 7.58:1 on the sheet.')}</span>
      </div>
    );
  }
  if (!isHex(hex)) {
    return <div className="adm-accent"><span className="adm-accent-verdict fail">{t('Not a colour yet — six hex digits after the #.')}</span></div>;
  }
  const rep = deriveAccent(hex);
  const ok = rep.problems.length === 0;
  const near = ok ? null : nearestPassing(hex);
  const rows: Array<[string, number]> = [
    [AA_SURFACES[0], rep.ratios.dayField],
    [AA_SURFACES[1], rep.ratios.daySheet],
    [AA_SURFACES[2], rep.ratios.dayInk],
    [AA_SURFACES[3], rep.ratios.nightField],
    [AA_SURFACES[4], rep.ratios.nightSheet],
    [AA_SURFACES[5], rep.ratios.nightInk],
  ];
  const Ramp = ({ r, dark }: { r: typeof rep.day; dark?: boolean }) => (
    <div className={`adm-ramp${dark ? ' dark' : ''}`}>
      <span className="u-label">{dark ? t('night') : t('day')}</span>
      <span className="sw fill" style={{ background: r.act, color: r.ink }} title={`act ${r.act} · ink ${r.ink}`}>Aa</span>
      <span className="sw" style={{ background: r.hover }} title={`hover ${r.hover}`} />
      <span className="sw" style={{ background: r.line }} title={`line ${r.line}`} />
      <span className="sw" style={{ background: r.soft }} title={`soft ${r.soft}`} />
      <span className="lnk-sample" style={{ color: r.act }}>{t('a link')} · {r.act}</span>
    </div>
  );
  return (
    <div className="adm-accent">
      <div className="adm-accent-ramps">
        <Ramp r={rep.day} />
        <Ramp r={rep.night} dark />
      </div>
      <table className="adm-aa">
        <thead><tr><th>{t('surface')}</th><th>{t('ratio')}</th><th>{t('AA {aa}:1', { aa: AA })}</th></tr></thead>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td className="k">{t(k)}</td>
              <td className={v >= AA ? 'pass' : 'fail'}>{v.toFixed(2)}:1</td>
              <td className={v >= AA ? 'pass' : 'fail'}>{v >= AA ? t('passes') : t('FAILS')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className={`adm-accent-verdict ${ok ? 'pass' : 'fail'}`}>
        {ok
          ? t('Passes AA on every surface — Save applies it at once, for everyone, on this and every next load.')
          : t('Save will be refused: {problems}.', { problems: rep.problems.join('; ') })}
      </span>
      {near ? (
        <span className="adm-accent-near">
          {t('The nearest shade of this hue that passes is')}{' '}
          <span className="adm-swatch" style={{ background: near }} /><Ref>{near}</Ref>
          {' '}<button type="button" className="lnkb" onClick={() => onPick(near)}>{t('use it')}</button>
        </span>
      ) : null}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

export default function AdminPage() {
  const t = useT();
  const { tp } = useLocale();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const toast = useToast();
  const nav = useEngagement();
  const openRoute = useOpenRoute(nav);
  const params = useRouteParams();

  // ── posture (polled; numbers are never blanked by a failed refresh) ──────
  const loadPosture = useCallback(() => adminConsoleApi.posture(), []);
  const posture = usePoll<Posture>(loadPosture, POLL_MS);
  const p = posture.data;

  // ── the trail's state ───────────────────────────────────────────────────
  const [realm, setRealm] = useState<'operator' | 'client'>('operator');
  const [action, setAction] = useState<string | null>(null);
  const [famFilter, setFamFilter] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [klass, setKlass] = useState<ConsequenceClass | null>(null);
  const [engagementFilter, setEngagementFilter] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [searchLive, setSearchLive] = useState('');
  const [page, setPage] = useState(1);
  const [openFams, setOpenFams] = useState<Set<string>>(() => new Set());
  const [openRow, setOpenRow] = useState<number | null>(null);

  const [vocab, setVocab] = useState<Vocabulary | null>(null);
  const [vocabError, setVocabError] = useState<string | null>(null);
  const [trail, setTrail] = useState<TrailPage | null>(null);
  const [trailError, setTrailError] = useState<string | null>(null);
  const [trailLoading, setTrailLoading] = useState(true);
  const firstTrailRead = useRef(true);

  // ── settings ────────────────────────────────────────────────────────────
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  // ── modals ──────────────────────────────────────────────────────────────
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitEmail, setAdmitEmail] = useState('');
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNext, setPwNext] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [withdraw, setWithdraw] = useState<{ id: number; email: string } | null>(null);
  const [openMode, setOpenMode] = useState(false);

  const err = useCallback(
    (e: unknown, fallback: string) => toast('err', e instanceof Error ? e.message : fallback),
    [toast],
  );

  // The settings form is seeded from the posture payload, so there is ONE producer of the
  // values and the Switches zone can never disagree with the rail cell that counts them.
  useEffect(() => {
    if (!p) return;
    const next: Record<string, string> = {};
    for (const s of p.settings) next[s.key] = s.value;
    setSavedValues(next);
    setValues((prev) => (Object.keys(prev).length ? prev : next));
  }, [p]);

  // ── loaders ─────────────────────────────────────────────────────────────

  const loadVocabulary = useCallback(async (which: 'operator' | 'client') => {
    try {
      setVocab(await adminConsoleApi.vocabulary(which));
      setVocabError(null);
    } catch (e) {
      setVocabError(e instanceof Error ? e.message : t('The vocabulary did not load'));
    }
  }, [t]);

  useEffect(() => { void loadVocabulary(realm); }, [loadVocabulary, realm]);

  const loadTrail = useCallback(async () => {
    setTrailLoading(true);
    try {
      // `first` is the ONLY read that records itself (`audit.query`). A filter click or a
      // second page never does — a reader that stamps on every keystroke fills the record it
      // is trying to make legible.
      const first = firstTrailRead.current;
      firstTrailRead.current = false;
      setTrail(await adminConsoleApi.trail({
        realm, action, family: famFilter, day, class: klass,
        engagement: engagementFilter, search: search || null, page, limit: 60, first,
      }));
      setTrailError(null);
    } catch (e) {
      setTrailError(e instanceof Error ? e.message : t('The trail did not load'));
    } finally {
      setTrailLoading(false);
    }
  }, [realm, action, famFilter, day, klass, engagementFilter, search, page, t]);

  useEffect(() => { void loadTrail(); }, [loadTrail]);

  // The search box debounces into the filter so every keystroke is not a query.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchLive.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchLive]);

  // ── arrival intents (F1 receiver) ───────────────────────────────────────
  //
  // `#admin?tab=audit&engagement=N` is the link Lifecycle's ledger routes into; `tab` also
  // keeps every old two-tab link alive by scrolling to the zone that absorbed it.
  useDeepLink(params, {
    tab: (v) => scrollToAnchor(v === 'settings' ? 'z-switches' : v === 'audit' ? 'z-trail' : 'z-posture'),
    engagement: (v) => {
      const n = Number(v);
      if (Number.isInteger(n) && n > 0) {
        setEngagementFilter(n);
        setPage(1);
        scrollToAnchor('z-trail');
      }
    },
  });

  // ── derived ─────────────────────────────────────────────────────────────

  const admins = p ? p.users.filter((u) => u.is_admin).length : 0;
  const emails = p?.allowed_emails ?? [];
  const accounts = p?.users ?? [];
  const tokens = p?.agent_tokens;
  const wired = p ? p.settings.filter((s) => s.consumers.length).length : 0;
  const modeKnown = !!p && p.registration_mode !== null;
  const closed = p?.registration_mode === 'allowlist';

  const lastDay = vocab?.days.length ? vocab.days[vocab.days.length - 1] : null;

  const rail = useMemo<AdmRailCell[]>(() => {
    if (!p) {
      return degradedRail(RAIL_KEYS, posture.error ?? t('the posture did not load'))
        .map((c, i) => ({
          ...c,
          key: RAIL_KEYS[i],
          anchor: 'z-posture',
          anchorLabel: 'Posture',
        }) as AdmRailCell);
    }
    const trailTotal = realm === 'client' ? p.audit.portal_total : p.audit.total;
    return [
      {
        key: 'REALM',
        value: (p.registration_mode ?? t('unknown')).toUpperCase(),
        unit: null, token: true,
        tone: !modeKnown ? 'unknown' : closed ? 'ink' : 'attend',
        detail: !modeKnown ? t('auth_settings did not answer')
          : closed ? tp(emails.length, '{n} email admitted', '{n} emails admitted')
            : t('anyone may register'),
        route: null, anchor: 'z-posture', anchorLabel: 'Posture',
      },
      {
        key: 'ACCOUNTS',
        value: String(accounts.length),
        unit: accounts.length === 1 ? t('operator') : t('operators'),
        tone: accounts.length ? 'ink' : 'nil',
        detail: accounts.length
          ? t('{admins} admin · {plain} plain', { admins, plain: accounts.length - admins })
          : t('none created yet'),
        route: null, anchor: 'z-posture', anchorLabel: 'Posture',
      },
      {
        key: 'AGENT KEYS',
        value: tokens?.known ? String(tokens.live) : '—',
        unit: tokens?.known ? t('live') : null,
        tone: !tokens?.known ? 'unknown' : tokens.live > 0 ? 'attend' : 'nil',
        detail: !tokens?.known ? t('the token tally did not run')
          : tokens.total ? t('{total} issued · {revoked} revoked', { total: tokens.total, revoked: tokens.revoked })
            : t('none issued'),
        route: null, anchor: 'z-posture', anchorLabel: 'Posture',
      },
      {
        key: 'SWITCHES',
        value: t('{wired} of {total}', { wired, total: p.settings.length }),
        unit: t('wired'),
        tone: wired < p.settings.length ? 'attend' : 'ink',
        detail: wired < p.settings.length
          ? t('{n} read by nothing', { n: p.settings.length - wired })
          : t('every key has a reader'),
        route: null, anchor: 'z-switches', anchorLabel: 'Switches',
      },
      {
        key: 'TRAIL',
        value: num(trailTotal),
        unit: t('events'),
        tone: trailTotal ? 'ink' : 'nil',
        detail: lastDay ? t('{n} on {date}', { n: num(lastDay.n), date: dmy(`${lastDay.d}T00:00`) }) : t('nothing in 30 days'),
        route: null, anchor: 'z-trail', anchorLabel: 'The trail',
      },
    ];
  }, [p, posture.error, modeKnown, closed, emails.length, accounts.length, admins, tokens, wired, lastDay, realm, t, tp]);

  const claims = useMemo<Claim[]>(() => {
    if (!p) return [];
    const orphans = accounts.filter(
      (a) => !emails.some((e) => (e.email ?? '').toLowerCase() === (a.email ?? '').toLowerCase()),
    );
    const me = accounts.find((a) => a.id === user?.id) ?? accounts[0];

    return [
      {
        id: 'mode',
        lamp: !modeKnown ? 'unknown' : closed ? 'ok' : 'attend',
        claim: !modeKnown
          ? t('The registration mode could not be read — this claim did NOT pass, it did not run.')
          : closed
            ? t('Registration is closed. Only an admitted email can create an operator account.')
            : t('Registration is OPEN — anyone who can reach the host can create an operator account.'),
        ev: [
          <Ref key="k">auth_settings.registration_mode</Ref>,
          <>= <Ref>{p.registration_mode ?? t('unknown')}</Ref></>,
          p.boot_reasserts_allowlist
            ? <>{t('reasserted on every boot by')} <Ref>index.ts</Ref></>
            : t('not reasserted on boot'),
        ],
        act: isAdmin && modeKnown && !closed
          ? <button type="button" className="btn btn-sm" onClick={() => setOpenMode(true)}>{t('Close it')}</button>
          : undefined,
      },
      {
        id: 'allowlist',
        lamp: emails.length === 0 ? 'attend' : emails.length === 1 ? 'ok' : 'attend',
        claim: emails.length === 0
          ? t('No email is admitted — with the door shut, nothing can create an account.')
          : emails.length === 1
            ? t('One email is admitted: {email}', { email: emails[0].email })
            : t('{n} emails are admitted.', { n: emails.length }),
        ev: [
          <Ref key="k">allowed_emails</Ref>,
          emails.length
            ? <>{t('since')} <Fig>{dmy(emails[0].created_at)}</Fig></>
            : t('empty'),
          ...(emails.length > 1
            ? [<span key="n" style={{ color: 'var(--attend)' }}>{t('newest: {email}', { email: emails[emails.length - 1].email })}</span>]
            : []),
        ],
        act: isAdmin && emails.length > 1
          ? (
            <button
              type="button" className="btn btn-sm"
              onClick={() => setWithdraw({ id: emails[emails.length - 1].id, email: emails[emails.length - 1].email })}
            >
              {t('Withdraw the newest')}
            </button>
          )
          : undefined,
      },
      {
        id: 'accounts',
        lamp: orphans.length ? 'attend' : accounts.length <= 1 ? 'ok' : 'attend',
        claim: accounts.length === 0
          ? t('No account exists yet — the first admitted email creates one at first login.')
          : orphans.length
            ? tp(orphans.length,
              '{n} account exists whose address is NOT on the allowlist — admitted, used, then withdrawn.',
              '{n} accounts exist whose address is NOT on the allowlist — admitted, used, then withdrawn.')
            : accounts.length === 1
              ? t('One account exists, and it is that email: {name}', { name: accounts[0].name })
              : t('{n} accounts exist. {admins} of them can write settings and read this trail.', { n: accounts.length, admins }),
        ev: [
          <Ref key="k">users</Ref>,
          accounts.length ? <><Fig>{admins}</Fig> {t('admin')}</> : <span className="n-none">—</span>,
          accounts.length
            ? (me?.has_password
              ? t('password set')
              : <span style={{ color: 'var(--attend)' }}>{t('no password — SSO only')}</span>)
            : '',
          ...(orphans.length ? [<Ref key="o">{orphans.map((o) => o.email ?? o.name).join(', ')}</Ref>] : []),
        ],
        act: accounts.length
          ? <button type="button" className="btn btn-sm" onClick={() => setPwOpen(true)}>{t('Change my password')}</button>
          : undefined,
      },
      {
        id: 'tokens',
        lamp: !tokens?.known ? 'unknown' : tokens.live === 0 ? 'ok' : 'attend',
        claim: !tokens?.known
          ? t('The agent-key tally did not run — whether anything outside this process can reach the tool surface is UNKNOWN.')
          : tokens.live === 0
            ? t('No agent key is live. Nothing outside this process can reach the engagement tool surface.')
            : tp(tokens.live, '{n} agent key live and can call the tool surface.', '{n} agent keys live and can call the tool surface.'),
        ev: [
          <Ref key="k">agent_tokens</Ref>,
          tokens?.known ? <><Fig>{tokens.total}</Fig> {t('issued')}</> : <span className="n-none">—</span>,
          tokens?.last_expiry
            ? <>{t('last expiry')} <Fig>{dmy(tokens.last_expiry)}</Fig></>
            : t('none issued'),
          p.agent_token_shape,
        ],
        act: isAdmin && tokens?.known && tokens.live > 0
          ? <button type="button" className="btn btn-sm" onClick={() => setRevokeOpen(true)}>{t('Revoke all')}</button>
          : undefined,
      },
      {
        id: 'portal',
        lamp: 'off',
        claim: !p.portal.known
          ? t('The client-account tally did not run.')
          : p.portal.users === 0
            ? t('No client account exists. The client realm shares nothing with this one — separate cookie, separate table, separate trail.')
            : t('{accounts} across {clients}.', {
              accounts: tp(p.portal.users, '{n} client account', '{n} client accounts'),
              clients: tp(p.portal.clients, '{n} client', '{n} clients'),
            }),
        ev: [
          <Ref key="k">portal_users</Ref>,
          <Ref key="c">signet.portal.sid</Ref>,
          <button
            key="r" type="button" className="adm-link"
            onClick={() => void openRoute(ROUTES.portalAdmin({}))}
          >
            {t('administered on the Client Portal tab')}
          </button>,
        ],
      },
    ];
  }, [p, accounts, emails, admins, modeKnown, closed, tokens, isAdmin, user?.id, openRoute, t, tp]);

  const claimsBad = claims.filter((c) => c.lamp === 'attend' || c.lamp === 'unknown').length;

  // SEALED is not a sixth opinion: it is exactly "every claim below holds". One producer, one
  // number (F9) — a masthead that said `sealed` while a lamp underneath it was amber would be
  // the same two-truths defect this programme exists to remove.
  const sealKnown = !!p && claims.every((c) => c.lamp !== 'unknown');
  const sealed = sealKnown && claimsBad === 0;

  const families = useMemo(() => familiesOf(vocab?.verbs ?? []), [vocab]);

  const chips = useMemo(() => {
    const out: { key: string; label: string; value: string; clear: () => void }[] = [];
    if (action) out.push({ key: 'verb', label: t('verb'), value: action, clear: () => { setAction(null); setPage(1); } });
    if (famFilter) out.push({ key: 'noun', label: t('noun'), value: famFilter, clear: () => { setFamFilter(null); setPage(1); } });
    if (day) out.push({ key: 'day', label: t('day'), value: dmy(`${day}T00:00`), clear: () => { setDay(null); setPage(1); } });
    if (klass) {
      const label = klass === 'reason' ? t('carries a written reason')
        : klass === 'tomb' ? t('destroyed a row') : t('produced a hash');
      out.push({ key: 'class', label: t('class'), value: label, clear: () => { setKlass(null); setPage(1); } });
    }
    if (engagementFilter) {
      const code = nav.engagements.find((e) => e.id === engagementFilter)?.code;
      out.push({
        key: 'engagement', label: t('engagement'), value: code ?? `#${engagementFilter}`,
        clear: () => { setEngagementFilter(null); setPage(1); },
      });
    }
    if (search) out.push({ key: 'search', label: t('text'), value: search, clear: () => { setSearch(''); setSearchLive(''); setPage(1); } });
    return out;
  }, [action, famFilter, day, klass, engagementFilter, search, nav.engagements, t]);

  const clearAll = useCallback(() => {
    setAction(null); setFamFilter(null); setDay(null); setKlass(null);
    setEngagementFilter(null); setSearch(''); setSearchLive(''); setPage(1);
  }, []);

  // ── acts ────────────────────────────────────────────────────────────────

  const refreshAll = useCallback(async () => {
    await posture.refresh();
    await loadVocabulary(realm);
    await loadTrail();
  }, [posture, loadVocabulary, realm, loadTrail]);

  const doAdmit = async () => {
    const email = admitEmail.trim().toLowerCase();
    if (!email.includes('@')) { toast('warn', t('That is not an email address')); return; }
    setBusy(true);
    try {
      await adminConsoleApi.admitEmail(email);
      setAdmitOpen(false); setAdmitEmail('');
      toast('ok', t('{email} is admitted — and the act is in the trail below', { email }));
      await refreshAll();
    } catch (e) { err(e, t('The email could not be admitted')); } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    if (!withdraw) return;
    setBusy(true);
    try {
      await adminConsoleApi.withdrawEmail(withdraw.id);
      toast('ok', t('{email} withdrawn', { email: withdraw.email }));
      setWithdraw(null);
      await refreshAll();
    } catch (e) { err(e, t('The address could not be withdrawn')); } finally { setBusy(false); }
  };

  const doClose = async () => {
    setBusy(true);
    try {
      await adminConsoleApi.setRegistrationMode('allowlist');
      setOpenMode(false);
      toast('ok', t('Registration is closed — only an admitted email can create an account'));
      await refreshAll();
    } catch (e) { err(e, t('The registration mode could not be changed')); } finally { setBusy(false); }
  };

  const doRevoke = async () => {
    setBusy(true);
    try {
      const out = await adminConsoleApi.revokeAllTokens();
      setRevokeOpen(false);
      toast('ok', tp(out.revoked, '{n} agent key revoked', '{n} agent keys revoked'));
      await refreshAll();
    } catch (e) { err(e, t('The keys could not be revoked')); } finally { setBusy(false); }
  };

  const doPassword = async () => {
    if (pwNext.length < 8) { toast('warn', t('The new password must be at least 8 characters')); return; }
    if (pwNext !== pwConfirm) { toast('warn', t('The two new passwords do not match')); return; }
    setBusy(true);
    try {
      await adminConsoleApi.changePassword(pwCurrent, pwNext);
      setPwOpen(false); setPwCurrent(''); setPwNext(''); setPwConfirm('');
      toast('ok', t('Your password is changed'));
    } catch (e) { err(e, t('The password could not be changed')); } finally { setBusy(false); }
  };

  const dirty = useMemo(
    () => Object.keys(savedValues).some((k) => (values[k] ?? '') !== (savedValues[k] ?? '')),
    [values, savedValues],
  );

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const changed = Object.fromEntries(
        Object.keys(savedValues)
          .filter((k) => (values[k] ?? '') !== (savedValues[k] ?? ''))
          .map((k) => [k, values[k] ?? '']),
      );
      const after = await adminConsoleApi.saveSettings(changed);
      setValues(after);
      setSavedValues(after);
      if ('accent_color' in changed) reloadThemeCss();
      toast('ok', tp(Object.keys(changed).length, '{n} switch saved', '{n} switches saved'));
      await refreshAll();
    } catch (e) { err(e, t('The switches could not be saved')); } finally { setSavingSettings(false); }
  };

  const openTrailRoute = useCallback((row: TrailRow) => {
    const r = row.route as Record<string, unknown> | null;
    if (r && r.kind === 'client') {
      void openRoute(ROUTES.client({ id: Number(r.client_id) }));
      return;
    }
    if (r && r.kind === 'in-page') {
      scrollToAnchor(String(r.anchor));
      return;
    }
    const resolved = resolveRouteRef(r);
    if (resolved.kind === 'dest') { void openRoute(resolved.dest); return; }
    if (resolved.kind === 'in-page') { scrollToAnchor(resolved.anchor); return; }
    if (resolved.kind === 'client-view') { void openRoute(ROUTES.clients()); return; }
    // `kind: 'none'` carries a sentence. Show it — never swallow it.
    toast('warn', row.route_reason ?? resolved.reason);
  }, [openRoute, toast]);

  const copyCitation = (row: TrailRow) => {
    void navigator.clipboard?.writeText(row.citation).catch(() => undefined);
    toast('ok', t('Citation copied'));
  };

  const trailQuery = {
    realm, action, family: famFilter, day, class: klass,
    engagement: engagementFilter, search: search || null,
  };

  // ── the page ────────────────────────────────────────────────────────────

  return (
    <div className="page adm">
      {/* ── masthead ─────────────────────────────────────────────────── */}
      <header className="mast">
        <div className="mast-top">
          <div className="mast-main">
            <h1 className="adm-h1">
              {t('Admin')}
              <i
                className={`adm-dot${!sealKnown ? ' adm-dot--unknown' : sealed ? '' : ' adm-dot--attend'}`}
                aria-hidden="true"
              />
              <span className="adm-state">{!sealKnown ? t('unknown') : sealed ? t('sealed') : t('open')}</span>
            </h1>
            <div className="mast-sub adm-sub">
              <Ref>signet</Ref>
              <span>{t("the system's own record — the one page whose subject is Signet")}</span>
              {posture.notice && <span style={{ color: 'var(--ink-4)' }}>{posture.notice}</span>}
              {!isAdmin && <span style={{ color: 'var(--attend)' }}>{t('read-only — you are not an admin')}</span>}
            </div>
          </div>
          <div className="mast-verbs">
            {/* No filled control in the masthead: nothing on this page is a once-a-visit
                commit. The loud thing lives in the zone that earns it. */}
            <a
              className="btn btn-sm"
              href={adminConsoleApi.exportHref(trailQuery)}
              onClick={() => toast('ok', t('The trail is downloading with the filters you have on'))}
            >
              {t('Export the trail')}
            </a>
          </div>
        </div>
      </header>

      {/* ── the rail — the kit instrument, riding the masthead rule ───── */}
      <div className="instr">
      <div className="rail">
        {rail.map((cell) => (
          <button
            key={cell.key}
            type="button"
            className="fld"
            onClick={() => scrollToAnchor(cell.anchor)}
          >
            <span className="fl u-label">{t(cell.key)}</span>
            <div className={`fv voice-figure${TONE_CLASS[cell.tone] ?? ''}`}>
              {cell.tone === 'unknown' && cell.value === '—'
                ? <span className="n-unknown" title={cell.detail}>—</span>
                : cell.token
                  ? <span className="tok">{cell.value}</span>
                  : <span>{cell.value}</span>}
              {cell.unit && <span className="u adm-unit">{cell.unit}</span>}
            </div>
            <div className="fd" title={cell.detail}>{cell.detail}</div>
            <div className="adm-fld-r">{t(cell.anchorLabel)} →</div>
          </button>
        ))}
      </div>
      </div>

      {/* ══ ZONE 1 · POSTURE ═══════════════════════════════════════════ */}
      <section className="zone" id="z-posture">
        <div className="zone-head">
          <h2 className="u-label">{t('Posture')}</h2>
          <span className={`zone-count${claimsBad ? ' zone-count--attend' : ''}`}>
            {posture.loading && !p ? t('reading')
              : claimsBad === 0
                ? t('{n} claims · all hold', { n: claims.length })
                : t('{bad} of {total} do not hold', { bad: claimsBad, total: claims.length })}
          </span>
          {isAdmin && (
            <div className="ml">
              <button type="button" className="btn btn-sm" onClick={() => setAdmitOpen(true)}>
                {t('Admit an email')}
              </button>
            </div>
          )}
        </div>

        {posture.error && !p ? (
          <EmptyState
            kind="error"
            title={t('The posture did not load')}
            detail={posture.error}
            onRetry={() => void posture.refresh()}
          />
        ) : !p ? (
          <div className="adm-post"><div className="adm-empty">{t("Reading the system's own state…")}</div></div>
        ) : (
          <>
            {p.problems.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {p.problems.map((pr) => (
                  <ProblemState
                    key={pr.source}
                    problems={p.problems}
                    source={pr.source}
                    zone={t('The {source} claim', { source: pr.source })}
                    onRetry={() => void posture.refresh()}
                  />
                ))}
              </div>
            )}
            <div className="adm-post">
              {claims.map((c) => (
                <div className="adm-post-r" key={c.id}>
                  <i
                    className={`adm-lamp${c.lamp === 'attend' ? ' adm-lamp--attend'
                      : c.lamp === 'off' ? ' adm-lamp--off'
                        : c.lamp === 'unknown' ? ' adm-lamp--unknown' : ''}`}
                    aria-hidden="true"
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="adm-claim">{c.claim}</div>
                    <div className="adm-ev">
                      {c.ev.filter(Boolean).map((e, i) => (
                        <span key={i} style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline' }}>
                          {i > 0 && <span className="sep">·</span>}
                          <span>{e}</span>
                        </span>
                      ))}
                    </div>
                    {c.id === 'allowlist' && emails.length > 1 && (
                      <div className="adm-ev">
                        {emails.map((e) => (
                          <Ref key={e.id}>{e.email}{e.is_admin ? ` · ${t('admin')}` : ''}</Ref>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="adm-post-act">{c.act}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ══ ZONE 2 · CONSEQUENCE ═══════════════════════════════════════ */}
      <section className="zone" id="z-cons">
        <div className="zone-head">
          <h2 className="u-label">{t('Consequence')}</h2>
          <span className="zone-count">
            {vocab?.classes && vocab.notes
              ? t('{decisions} of {total} entries are a decision · the rest are keystrokes', {
                decisions: num(vocab.notes.authored + vocab.classes.tomb + vocab.classes.hash),
                total: num(vocab.total),
              })
              : t('the tallies did not run')}
          </span>
        </div>

        {realm === 'client' ? (
          <EmptyState
            kind="empty"
            title={t('The client realm has no consequence classes')}
            detail={
              <>
                <code>portal_audit</code> {t('records reading, not deciding: a client can view, comment and run a test case, and none of those destroys a row or produces a hash. Switch the trail back to the operator realm to see the three memberships.')}
              </>
            }
          />
        ) : vocabError || !vocab?.classes || !vocab.notes ? (
          <EmptyState
            kind="error"
            title={t('The consequence tallies did not load')}
            detail={vocabError ?? t('The vocabulary payload carried no tallies.')}
            onRetry={() => void loadVocabulary(realm)}
          />
        ) : (
          <>
            <div className="adm-cons">
              <div className="adm-cons-c adm-cons-c--loud">
                <div className="adm-cons-n voice-figure">
                  {vocab.notes.authored}<small>{t('of {n}', { n: num(vocab.total) })}</small>
                </div>
                <div className="u-label">{t('Carry a reason you typed')}</div>
                <div className="adm-cons-t">
                  {t('Every place you went round a guarantee and had to say why.')}{' '}
                  <Fig>{vocab.notes.templated_rows}</Fig> {t('more rows carry a note, but it is one of')}{' '}
                  <Fig>{vocab.notes.templates}</Fig> {t('constants the server writes — those are the system speaking, and they are not here.')}
                </div>
                <div className="adm-cons-f">
                  <button
                    type="button" className="btn btn-sm"
                    onClick={() => { setKlass('reason'); setAction(null); setFamFilter(null); setDay(null); setPage(1); scrollToAnchor('z-trail'); }}
                  >
                    {t('Show them in the trail →')}
                  </button>
                </div>
              </div>

              <div className="adm-cons-c">
                <div className="adm-cons-n voice-figure">
                  {num(vocab.classes.tomb)}<small>{t('of {n}', { n: num(vocab.total) })}</small>
                </div>
                <div className="u-label">{t('Destroyed a row')}</div>
                <div className="adm-cons-t">
                  {t('Including')} <Fig>{vocab.classes.client_deletes}</Fig> {t('clients and')}{' '}
                  <Fig>{vocab.classes.engagement_deletes}</Fig> {t('engagements deleted with their cascades. The')}{' '}
                  <Ref>before</Ref> {t('blob in the entry is the only surviving copy of each —')}{' '}
                  <Fig>{num(vocab.classes.tomb_chars)}</Fig> {t('characters of record that exists nowhere else in the database.')}
                </div>
                <div className="adm-cons-f">
                  <button
                    type="button" className="btn btn-sm"
                    onClick={() => { setKlass('tomb'); setAction(null); setFamFilter(null); setDay(null); setPage(1); scrollToAnchor('z-trail'); }}
                  >
                    {t('Show them in the trail →')}
                  </button>
                </div>
              </div>

              <div className="adm-cons-c">
                <div className="adm-cons-n voice-figure">
                  {vocab.classes.hash}<small>{t('of {n}', { n: num(vocab.total) })}</small>
                </div>
                <div className="u-label">{t('Produced a hash a signature stands on')}</div>
                <div className="adm-cons-t">
                  {t('Signatures, baselines and issued versions.')} <em>{t('The baseline is sacred')}</em>{' '}
                  {t('— these are the entries an argument about scope is settled on, and the content hash in each is rendered whole, never truncated.')}
                </div>
                <div className="adm-cons-f">
                  <button
                    type="button" className="btn btn-sm"
                    onClick={() => { setKlass('hash'); setAction(null); setFamFilter(null); setDay(null); setPage(1); scrollToAnchor('z-trail'); }}
                  >
                    {t('Show them in the trail →')}
                  </button>
                </div>
              </div>
            </div>
            <p className="adm-read" style={{ marginTop: 12, fontSize: 13.5 }}>
              {t('Membership is a query, not an opinion: a row is here when it carries a written reason, when it destroyed a row whose only surviving copy is the entry itself, or when it produced a hash that a signature depends on. Everything else is a keystroke and lives in the trail.')}
            </p>
          </>
        )}
      </section>

      {/* ══ ZONE 3 · THE TRAIL ═════════════════════════════════════════ */}
      <section className="zone" id="z-trail">
        <div className="zone-head">
          <h2 className="u-label">{t('The trail')}</h2>
          <span className="zone-count">
            {vocab
              ? realm === 'client'
                ? <>{t('{n} client-realm events', { n: num(vocab.total) })} · <Fig>{vocab.with_ip}</Fig> {t('carry an IP')}</>
                : <>{t('{n} operator-realm events · {verbs} verbs', { n: num(vocab.total), verbs: vocab.verbs.length })} · <span className="n-zero">{vocab.with_ip}</span> {t('carry an IP')}</>
              : t('reading')}
          </span>
        </div>

        <div className="adm-trail">
          {/* the verb catalogue */}
          <aside className="adm-cat">
            <div className="adm-cat-h">
              <span className="u-label">{t('What happened')}</span>
              <span className="zone-count" style={{ marginLeft: 'auto' }}>
                {vocab ? t('{verbs} verbs · {nouns} nouns', { verbs: vocab.verbs.length, nouns: families.length }) : '—'}
              </span>
            </div>
            <div className="adm-cat-scroll">
              {vocabError ? (
                <div className="adm-empty">
                  <strong>{t('The vocabulary did not load.')}</strong>
                  {vocabError}
                  <div>
                    <button type="button" className="btn btn-sm" onClick={() => void loadVocabulary(realm)}>{t('Try again')}</button>
                  </div>
                </div>
              ) : families.length === 0 ? (
                <div className="adm-empty">{t('Nothing has been recorded in this realm yet.')}</div>
              ) : families.map((f) => {
                const isOpen = openFams.has(f.fam);
                return (
                  <div className="adm-fam" key={f.fam}>
                    <button
                      type="button"
                      className={`adm-fam-h${famFilter === f.fam ? ' on' : ''}`}
                      onClick={() => {
                        setOpenFams((prev) => {
                          const next = new Set(prev);
                          if (next.has(f.fam)) next.delete(f.fam); else next.add(f.fam);
                          return next;
                        });
                      }}
                      onDoubleClick={() => {
                        setFamFilter(famFilter === f.fam ? null : f.fam);
                        setAction(null); setPage(1);
                      }}
                      title={t('{fam} — click to open, double-click to filter the trail to all {n} of them', { fam: f.fam, n: f.n })}
                    >
                      <span className="adm-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                      <span className="adm-fam-name">{f.fam}</span>
                      <span className="adm-fam-n voice-figure">{num(f.n)}</span>
                    </button>
                    {isOpen && (
                      <div className="adm-verbs-list">
                        {f.verbs.map((v) => (
                          <button
                            key={v.action}
                            type="button"
                            className={`adm-verb${action === v.action ? ' on' : ''}`}
                            title={t('{action} · last {date}', { action: v.action, date: dmy(String(v.last)) })}
                            onClick={() => {
                              setAction(action === v.action ? null : v.action);
                              setFamFilter(null); setKlass(null); setPage(1);
                            }}
                          >
                            <span className="adm-verb-v">{v.action.split('.').slice(1).join('.') || v.action}</span>
                            <span className="adm-verb-n voice-figure">{v.n}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* the log */}
          <div className="adm-log">
            <div className="adm-log-h">
              <div className="adm-seg">
                {(['operator', 'client'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={realm === r ? 'on' : ''}
                    onClick={() => {
                      if (realm === r) return;
                      setRealm(r); clearAll(); setOpenFams(new Set()); setOpenRow(null);
                    }}
                  >
                    {r === 'operator' ? t('Operator') : t('Client')}
                  </button>
                ))}
              </div>
              <span className="voice-system">
                {realm === 'client'
                  ? <><Ref>portal_audit</Ref> — {t('actor, IP and user-agent on every row')}</>
                  : <><Ref>audit_log</Ref> — {t('the manual logger takes no IP, so the column is empty by construction')}</>}
              </span>
              <div className="adm-days">
                {(vocab?.days ?? []).map((b) => {
                  const max = Math.max(...(vocab?.days ?? []).map((x) => x.n), 1);
                  return (
                    <button
                      key={b.d}
                      type="button"
                      className={`adm-day${day === b.d ? ' on' : ''}`}
                      title={tp(b.n, '{n} event on {date}', '{n} events on {date}', { date: dmy(`${b.d}T00:00`) })}
                      onClick={() => { setDay(day === b.d ? null : b.d); setPage(1); }}
                    >
                      <span className="adm-day-bar" style={{ height: Math.max(3, Math.round((b.n / max) * 26)) }} />
                      <span className="adm-day-l">{dm(b.d)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="adm-filterbar">
              {chips.length === 0 ? (
                <span>
                  {t('Newest first')} · {trail ? t('{n} entries', { n: num(trail.total) }) : t('reading')}
                </span>
              ) : (
                <>
                  <span>
                    <Fig>{num(trail?.total ?? 0)}</Fig> {t('of')} <Fig>{num(vocab?.total ?? 0)}</Fig> —
                  </span>
                  {chips.map((c) => (
                    <span className="adm-chip" key={c.key}>
                      <span style={{ opacity: .7 }}>{c.label}</span>{c.value}
                      <button type="button" title={tCtx(t, 'clear', 'input')} onClick={c.clear}>×</button>
                    </span>
                  ))}
                  <button type="button" className="btn btn-sm" onClick={clearAll}>{t('Clear all')}</button>
                </>
              )}
              <input
                className="adm-search"
                style={{ marginLeft: 'auto' }}
                placeholder={t('Search the details…')}
                value={searchLive}
                onChange={(e) => setSearchLive(e.target.value)}
                aria-label={t('Search the trail')}
              />
            </div>

            <div className="adm-rows">
              {trailError ? (
                <EmptyState
                  kind="error"
                  title={t('The trail did not load')}
                  detail={trailError}
                  onRetry={() => void loadTrail()}
                />
              ) : trailLoading && !trail ? (
                <div className="adm-empty">{t('Reading the record…')}</div>
              ) : (trail?.rows.length ?? 0) === 0 ? (
                chips.length ? (
                  <EmptyState
                    kind="no-match"
                    title={t('Nothing matches')}
                    detail={t('This combination exists in the vocabulary but not in the record. The catalogue on the left carries a count for every verb, so the nearest thing with rows is the one with the largest number.')}
                    onClear={clearAll}
                    clearLabel={t('Clear the filters')}
                  />
                ) : (
                  <EmptyState
                    kind="empty"
                    title={t('Nothing has been recorded in this realm')}
                    detail={t('Every mutation in Signet writes one row here. An empty trail means nothing has been written yet — not that the recorder is off.')}
                  />
                )
              ) : trail!.rows.map((row) => {
                const isOpen = openRow === row.id;
                const d = diffOf(row.details);
                return (
                  <div className={`adm-row${isOpen ? ' open' : ''}`} key={row.id}>
                    <button
                      type="button"
                      className="adm-row-h"
                      onClick={() => setOpenRow(isOpen ? null : row.id)}
                    >
                      <span className="adm-row-t">{dm(row.created_at.slice(0, 10))} {hm(row.created_at)}</span>
                      <span className={`adm-row-a${row.actor ? '' : ' sys'}`}>
                        {row.actor ?? (realm === 'client' ? t('client realm') : t('system · agent'))}
                      </span>
                      <span className="adm-row-c">
                        <Line pieces={row.pieces} />
                        {row.klass && (
                          <span className="adm-marks">
                            <span className={`adm-mk adm-mk--${row.klass}`}>
                              {row.klass === 'reason' ? t('reason') : row.klass === 'tomb' ? t('only copy') : t('hash')}
                            </span>
                          </span>
                        )}
                      </span>
                      <span className="adm-row-v">{row.action}</span>
                    </button>

                    {isOpen && (
                      <div className="adm-det">
                        {row.note && (
                          <div className="adm-det-note">
                            {row.note.authored ? (
                              <>
                                <span className="u-label">{t('The reason, as written')}</span>
                                <div className="ev" style={{ marginTop: 6 }}>
                                  {row.note.text}
                                  <span className="ev-src">{row.citation}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="u-label">
                                  {tp(row.note.multiplicity,
                                    'Note — a constant the server writes ({n} row carries this exact string)',
                                    'Note — a constant the server writes ({n} rows carry this exact string)')}
                                </span>
                                <div className="voice-system" style={{ marginTop: 4, maxWidth: '78ch' }}>
                                  {row.note.text}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {d.keys.length > 0 ? (
                          <div className="adm-det-grid">
                            <div className="adm-dk">{t('field')}</div>
                            <div className="adm-dk">{t('before')}</div>
                            <div className="adm-dk">{t('after')}</div>
                            {d.keys.map((k) => {
                              const was = d.before[k];
                              const now = d.after[k];
                              const changed = JSON.stringify(was) !== JSON.stringify(now);
                              return (
                                <div style={{ display: 'contents' }} key={k}>
                                  <div className={`adm-dk${changed ? ' adm-chg' : ''}`}>{k}</div>
                                  <div className={`adm-dv${changed && was !== undefined ? ' adm-dv--was' : ''}${changed ? ' adm-chg' : ''}`}>
                                    {was === undefined ? <span className="n-none">—</span> : <DiffValue v={was} />}
                                  </div>
                                  <div className={`adm-dv${changed ? ' adm-dv--now adm-chg' : ''}`}>
                                    {now === undefined ? <span className="n-none">—</span> : <DiffValue v={now} />}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : !row.note ? (
                          <div className="voice-system" style={{ marginTop: 10 }}>
                            {t('This entry records the act and nothing else — no before/after was captured.')}
                          </div>
                        ) : null}

                        {d.scalars.length > 0 && (
                          <div className="adm-ev" style={{ marginTop: 9 }}>
                            {d.scalars.map(([k, v], i) => (
                              <span key={k} style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}>
                                {i > 0 && <span className="sep">·</span>}
                                <Ref>{k}</Ref>
                                {typeof v === 'number' ? <Fig>{v}</Fig> : <span className="adm-dv">{String(v)}</span>}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="adm-det-foot">
                          <span className="adm-cite">{row.citation}</span>
                          <button type="button" className="btn btn-sm" onClick={() => copyCitation(row)}>
                            {t('Copy the citation')}
                          </button>
                          {row.route ? (
                            <button type="button" className="btn btn-sm" onClick={() => openTrailRoute(row)}>
                              {t('Open {what} →', {
                                what: `${row.resource_type.replace(/_/g, ' ')}${row.resource_id ? ` ${row.resource_id}` : ''}`,
                              })}
                            </button>
                          ) : (
                            <span className="adm-noroute">{row.route_reason ?? t('This entry carries no route.')}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {trail && trail.total > trail.limit && (
              <div className="adm-paging">
                <span>
                  <Fig>{num((trail.page - 1) * trail.limit + 1)}</Fig>–
                  <Fig>{num(Math.min(trail.page * trail.limit, trail.total))}</Fig> {t('of')}{' '}
                  <Fig>{num(trail.total)}</Fig>
                </span>
                <div className="ml">
                  <button
                    type="button" className="btn btn-sm" disabled={trail.page <= 1}
                    onClick={() => setPage((n) => Math.max(1, n - 1))}
                  >
                    {t('← Newer')}
                  </button>
                  <button
                    type="button" className="btn btn-sm"
                    disabled={trail.page * trail.limit >= trail.total}
                    onClick={() => setPage((n) => n + 1)}
                  >
                    {t('Older →')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ ZONE 4 · SWITCHES ══════════════════════════════════════════ */}
      <section className="zone" id="z-switches">
        <div className="zone-head">
          <h2 className="u-label">{t('Switches')}</h2>
          <span className="zone-count">
            {p
              ? t('{wired} of {total} are read by anything', { wired, total: p.settings.length })
              : t('reading')}
          </span>
          <div className="ml">
            {/* A control with nothing to control is a display: Save and Revert exist only
                while something is actually dirty. */}
            {dirty && isAdmin ? (
              <>
                <button
                  type="button" className="btn btn-sm" disabled={savingSettings}
                  onClick={() => setValues(savedValues)}
                >
                  {t('Revert')}
                </button>
                <button
                  type="button" className="btn btn-sm btn-accent" disabled={savingSettings}
                  onClick={() => void saveSettings()}
                >
                  {savingSettings ? t('Saving…') : t('Save')}
                </button>
              </>
            ) : (
              <span className="voice-system">{t('nothing edited')}</span>
            )}
          </div>
        </div>

        {posture.error && !p ? (
          <EmptyState
            kind="error"
            title={t('The switches did not load')}
            detail={posture.error}
            onRetry={() => void posture.refresh()}
          />
        ) : !p ? (
          <div className="adm-sw"><div className="adm-empty">{t('Reading…')}</div></div>
        ) : p.settings.length === 0 ? (
          <EmptyState kind="empty" title={t('This build exposes no settings')} detail={t('The whitelist in routes/admin.ts is empty.')} />
        ) : (
          <div className="adm-sw">
            {p.settings.map((s: SettingRow) => {
              const dead = s.consumers.length === 0;
              return (
                <Fragment key={s.key}>
                <div className={`adm-sw-r${dead ? ' adm-sw-r--dead' : ''}`}>
                  <div>
                    <div className="adm-sw-k">{s.key}</div>
                    <div className="adm-sw-lab">{s.label}</div>
                  </div>
                  <div className="adm-sw-val">
                    {s.key === 'accent_color' ? (
                      <AccentInput
                        value={values[s.key] ?? s.value}
                        disabled={!isAdmin}
                        onChange={(v) => setValues((prev) => ({ ...prev, [s.key]: v }))}
                      />
                    ) : s.key === 'default_commercial_model' ? (
                      <select
                        value={values[s.key] ?? s.value}
                        disabled={!isAdmin}
                        aria-label={s.label}
                        onChange={(e) => setValues((prev) => ({ ...prev, [s.key]: e.target.value }))}
                      >
                        {['fixed', 'tm', 'capped_tm', 'retainer'].map((m) => (
                          <option key={m} value={m}>{tCtx(t, m, 'model')}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={values[s.key] ?? s.value}
                        disabled={!isAdmin}
                        spellCheck={false}
                        aria-label={s.label}
                        placeholder={s.default}
                        onChange={(e) => setValues((prev) => ({ ...prev, [s.key]: e.target.value }))}
                      />
                    )}
                  </div>
                  <div className="adm-sw-con">
                    {dead ? (
                      <><b>{t('Nothing reads this.')}</b> {s.dead_note}</>
                    ) : (
                      <>{t('Read by')} {s.consumers.map((c, i) => (
                        <span key={c}>{i > 0 && ', '}<Ref>{c}</Ref></span>
                      ))}.</>
                    )}
                  </div>
                  <div>
                    <span className={`adm-badge ${dead ? 'adm-badge--dead' : 'adm-badge--live'}`}>
                      {dead ? t('no consumer') : t('wired')}
                    </span>
                  </div>
                </div>
                {s.key === 'accent_color' ? (
                  <AccentPreview
                    value={values[s.key] ?? s.value}
                    onPick={(v) => { if (isAdmin) setValues((prev) => ({ ...prev, accent_color: v })); }}
                  />
                ) : null}
                </Fragment>
              );
            })}
          </div>
        )}

        {/* The record: why the first accent control went, and what this one does differently. */}
        <div className="adm-note">
          <button type="button" className="adm-note-bar" onClick={() => setNoteOpen((v) => !v)}>
            <span className="adm-caret" aria-hidden="true">{noteOpen ? '▾' : '▸'}</span>
            <span className="u-label" style={{ color: 'var(--ink-3)' }}>
              {t("How the accent holds ATTEST's contrast law")}
            </span>
            <span className="zone-count" style={{ marginLeft: 'auto' }}>
              {t('the first control was removed; this one is gated')}
            </span>
          </button>
          {noteOpen && (
            <div className="adm-note-body">
              <p>
                {t('The first')} <Ref>accent_color</Ref> {t('derived its ramp on')} <em>{t('mount')}</em> {t('and wrote it inline on')}{' '}
                <Ref>&lt;html&gt;</Ref> {t('— so the tint applied only after someone opened Admin, drifted per session, never carried')}{' '}
                <Ref>--act-ink</Ref>{t(', and three of its five swatches failed AA with nothing on the write path but')}{' '}
                <Ref>/^#[0-9a-fA-F]&#123;6&#125;$/</Ref>.
              </p>
              <p>
                {t('Now: one exact colour.')} <Ref>src/shared/accent.ts</Ref> {t('derives the whole ramp —')}{' '}
                <Ref>--act</Ref>, <Ref>--act-hover</Ref>, <Ref>--act-soft</Ref>, <Ref>--act-line</Ref>,{' '}
                <Ref>--act-ink</Ref>{t(', for day and for night (the same hue lifted to ≥ 8:1 on the night field) — and measures the six ratios you see above. The server runs the same code on the write path and refuses anything under {aa}:1 on the field, the sheet or a filled control. What passes is served as', { aa: AA })}{' '}
                <Ref>/theme.css</Ref>{t(', linked after')} <Ref>attest.css</Ref> {t('in both shells and read at boot before first paint: no inline styles, one tint everywhere, from the login page on.')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── modals ───────────────────────────────────────────────────── */}

      {admitOpen && (
        <div className="modal-overlay" onClick={() => setAdmitOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Admit an email')}</h2>
              <button className="close-btn" onClick={() => setAdmitOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-ink-3 mb-12">
                {t('An admitted address may create ONE operator account at its first login. Nothing else changes: the address gets no admin rights, and withdrawing it later does not delete the account it created.')}
              </p>
              <input
                className="form-input"
                autoFocus
                placeholder="name@example.com"
                value={admitEmail}
                onChange={(e) => setAdmitEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void doAdmit(); }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAdmitOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={busy} onClick={() => void doAdmit()}>
                {t('Admit it')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwOpen && (
        <div className="modal-overlay" onClick={() => setPwOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('Change my password')}</h2>
              <button className="close-btn" onClick={() => setPwOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="pw-cur">{t('Current password')}</label>
                <input id="pw-cur" className="form-input" type="password" value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pw-new">{t('New password')}</label>
                <input id="pw-new" className="form-input" type="password" value={pwNext}
                  onChange={(e) => setPwNext(e.target.value)} />
                <div className="text-xs text-ink-3 mt-4">{t('At least 8 characters.')}</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pw-conf">{t('New password again')}</label>
                <input id="pw-conf" className="form-input" type="password" value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPwOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={busy} onClick={() => void doPassword()}>
                {t('Change it')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={revokeOpen}
        title={t('Revoke every live agent key')}
        danger
        confirmLabel={t('Revoke them')}
        onCancel={() => setRevokeOpen(false)}
        onConfirm={() => void doRevoke()}
      >
        <p className="text-sm text-ink-2">
          {t('Every unrevoked')} <code>agent_tokens</code> {t('row is marked revoked. A run in flight loses the tool surface at its next call and will fail; nothing already written is undone. New keys are minted per run, so the next dispatch works normally.')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={openMode}
        title={t('Close registration')}
        confirmLabel={t('Close it')}
        onCancel={() => setOpenMode(false)}
        onConfirm={() => void doClose()}
      >
        <p className="text-sm text-ink-2">
          <code>registration_mode</code> {t('becomes')} <code>allowlist</code>{t('. Accounts that already exist keep working; only the creation of NEW accounts is restricted to admitted addresses.')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={!!withdraw}
        title={t('Withdraw an admitted email')}
        danger
        confirmLabel={t('Withdraw it')}
        onCancel={() => setWithdraw(null)}
        onConfirm={() => void doWithdraw()}
      >
        <p className="text-sm text-ink-2">
          <code>{withdraw?.email}</code> {t('can no longer create an operator account. If it already created one, that account keeps working — this is the door, not the room.')}
        </p>
      </ConfirmModal>
    </div>
  );
}
